import FocusLock from 'react-focus-lock';
import { FormEventHandler, useImperativeHandle, useRef } from 'react';
import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverFooter,
  PopoverHeader,
  forwardRef,
  useMergeRefs,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';

type ColumnDropConfirmationPopoverProps = {
  onConfirm: (isSum: boolean, columnName?: string) => void;
  existingColumnNames: string[];
  columnId: string;
  property:
    | {
        category: string;
        propertyName: string;
        displayName: string;
      }
    | undefined;
};

type ColumnDropConfirmationFormValues = {
  isSum: boolean;
  columnName: string;
};

export const ColumnDropConfirmationForm = forwardRef(
  function ColumnDropConfirmationForm(
    {
      onConfirm,
      columnId,
      property,
      existingColumnNames,
    }: ColumnDropConfirmationPopoverProps,
    forwardedRef
  ): React.ReactElement {
    const isQuantityColumn = columnId === 'quantity';
    const isAddColumn = columnId === 'addColumn';

    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm<ColumnDropConfirmationFormValues>({
      defaultValues: {
        columnName: '',
        isSum: true,
      },
      values: {
        columnName: property?.displayName ?? '',
        isSum: true,
      },
      mode: 'onChange',
    });

    const columnNameInputRef = useRef<HTMLInputElement>(null);
    const sumInputRef = useRef<HTMLInputElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(
      forwardedRef,
      () => ({
        focus() {
          if (isAddColumn && columnNameInputRef.current) {
            columnNameInputRef.current.focus();
          } else if (!isQuantityColumn && sumInputRef.current) {
            sumInputRef.current.focus();
          } else if (submitButtonRef.current) {
            submitButtonRef.current.focus();
          }
        },
      }),
      [isQuantityColumn, isAddColumn]
    );

    const onSubmit: FormEventHandler<HTMLFormElement> = handleSubmit(
      ({ isSum, columnName }) => {
        const sumValues = isQuantityColumn || isSum;
        onConfirm(sumValues, columnName);
      }
    );

    // This "juggling" of refs is to make sure that to ensure we focus the correct Input
    // when opening, which is slightly different depending on the column we are dealing with here
    const { ref: columnNameFormRef, ...columnNameRegister } = register(
      'columnName',
      {
        setValueAs: (value) => value.trim(),
        validate: (value) => {
          if (value === '') {
            return 'Column name cannot be empty';
          } else if (
            isAddColumn &&
            existingColumnNames.some((name) => name === value)
          ) {
            return 'This column already exists';
          }
        },
      }
    );
    const { ref: isSumFormRef, ...isSumRegister } = register('isSum', {
      disabled: isQuantityColumn,
    });

    const mergedColumnNameRef = useMergeRefs(
      columnNameFormRef,
      columnNameInputRef
    );
    const mergedIsSumRef = useMergeRefs(isSumFormRef, sumInputRef);

    return (
      <FocusLock returnFocus persistentFocus={false}>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader>
          {isAddColumn ? 'Add new column' : 'Update column'}
        </PopoverHeader>
        <PopoverBody>
          <form onSubmit={onSubmit} id={`confirm-drop-${columnId}-form`}>
            <Flex direction="column" gap={4}>
              <FormControl
                display={isAddColumn ? 'block' : 'none'}
                isInvalid={!!errors.columnName}
              >
                <FormLabel>Column name</FormLabel>
                <Input
                  {...columnNameRegister}
                  ref={mergedColumnNameRef}
                  size="sm"
                />
                <FormErrorMessage>
                  {errors.columnName?.message}
                </FormErrorMessage>
              </FormControl>
              <Checkbox {...isSumRegister} ref={mergedIsSumRef}>
                Sum values
              </Checkbox>
            </Flex>
          </form>
        </PopoverBody>
        <PopoverFooter
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
        >
          <Button
            type="submit"
            size="xs"
            form={`confirm-drop-${columnId}-form`}
            ref={submitButtonRef}
          >
            Confirm
          </Button>
        </PopoverFooter>
      </FocusLock>
    );
  }
);
